/**
 * Putting an indicator on a chart, and taking it off again — as geometry.
 *
 * Pure, like `layout-templates.ts` and for the same reason: `mintId` is injected
 * rather than imported, because minting is the one thing that would make the
 * result impossible to assert on. The component computes the new panes with
 * these functions and dispatches plain data; nothing here knows React, a URL, or
 * a colour.
 *
 * ## Placement comes from the catalog, never from the user
 *
 * A 20-day moving average is in dollars and belongs over the candles; RSI is
 * bounded 0–100 and on the price axis is a flat line at the bottom of the pane.
 * That follows from what the indicator *is*, so the server declares it
 * (`services/qv-data/app/qf/chart_indicators.py`) and this places the plots
 * without deciding anything:
 *
 * - **`price`** appends plots to the existing price pane, on **that pane's own
 *   axis**. No axis is created — sharing the price scale is what makes an
 *   overlay an overlay, and a second axis would draw a moving average against a
 *   range of its own, next to but not on the candles.
 * - **`own`** appends one new pane with one axis, holding every output of the
 *   family. All of them together: a signal line in a different pane from its
 *   MACD line is meaningless.
 *
 * ## The alias is the contract with the data feed
 *
 * `<outputName>_<p1>_<p2>…`, parameters in the family's declared order —
 * `ema_20`, `macd_12_26_9`, `bbandsUpper_20_2`. `lib/chart-feed.ts` turns every
 * non-OHLC `outputAlias` into a data request and keys the response back by the
 * same string, and `qf/chart_indicators.parse_alias` reads it at the other end.
 * A plot built with a different alias is a plot with no data, which renders as
 * an empty series rather than as an error.
 *
 * **`volumeByPrice` is the one plot whose alias nothing answers.** It carries one
 * for the two reasons every other plot does — the alias names the family and its
 * parameters through a reload, and it is what {@link editIndicatorParams}
 * rewrites — but its bins are computed on the client from the bars the candles
 * already loaded, so there is no column behind it. The whole of that exception
 * lives in `chart-feed.ts`'s `plotFeeds`, which skips the *type* rather than the
 * alias. Nothing here has to know; it is written down because a reader who
 * assumes an alias reaches the wire is right about every other family.
 *
 * Strokes are passed through as **tokens** (`series1`…), never resolved here:
 * the chart builder resolves them against the active theme at render (D13), so
 * a theme switch repaints an indicator that was added under another one.
 */

import type {
  AxisInfo,
  ChartGroup,
  Pane,
  Plot,
  PlotPalette,
  PlotStyle,
  PlotType,
} from "@qufolio/model";
import type { IndicatorEntry, IndicatorOutputSpec } from "../api/indicators";
import {
  joinScale,
  matchingScale,
  movePlotsToPane,
  splitSmoothed,
  type SmoothKind,
} from "./move-to-pane";
import { newVolumePane } from "./chart-group";
import { withoutUnusedAxes } from "./orphan-axes";

/** Column names the price and volume panes are fed by, which are not indicators. */
const BASE_ALIASES = new Set(["ohlc", "open", "high", "low", "close", "volume"]);

/**
 * The one base alias that gets a row of its own.
 *
 * Volume is the chart's furniture rather than something added from the catalog,
 * but it *is* a pane — so leaving it out of the list meant the one surface that
 * enumerates the stack could not show, style or reorder it, while listing every
 * oscillator beside it. The other base aliases are the candles themselves and
 * have no pane and no style to edit.
 */
const VOLUME_ALIAS = "volume";

/**
 * The aliases the price series itself is drawn from.
 *
 * `haOhlc` is here and not in {@link BASE_ALIASES} because Heikin-Ashi candles
 * were only ever skipped by the *type* check below; naming them here is what
 * lets the price row survive a switch to a line chart, where the type is no
 * longer `candlestick` but the alias has not moved.
 */
const PRICE_ALIASES = new Set(["ohlc", "haOhlc", "open", "high", "low", "close"]);

/**
 * `bbandsUpper_20_2` → head `bbandsUpper`, params `[20, 2]`.
 *
 * The same shape the server parses (`parse_alias` in `chart_indicators.py`), so
 * a client cannot mint a string its own reader would not accept.
 *
 * **The head may contain digits**, and the head-only form has no params at all:
 * `pivotR1` is one output of the pivot family, not `pivotR` called with 1. The
 * pattern was `[A-Za-z]+`, which failed to match those outright — server-side
 * that silently dropped six pivot levels from every response, and here it broke
 * only the *grouping*, so each level listed as its own row in the picker rather
 * than one "Pivot Points" family. Two different symptoms, one assumption.
 *
 * **A parameter may be a word**, which is what lets a dropdown reach an alias:
 * `seasPath_ytd_10` is the seasonality path anchored at the turn of the year.
 * Everything else on this side already handled it — `resolveParams` branches on
 * `kind === "choice"` and `outputAlias` stringifies whatever it is given — so
 * this pattern was the single thing making the grammar numbers-only, and the
 * reason `riskAdjusted` had to escape through `aliasTemplate` and a `#`. The
 * server validates the word against that parameter's own `choices`; this end
 * only has to carry it without mangling the family grouping, which `[2]` does.
 *
 * **And a number may be negative.** No control emits one today. The cone's
 * `offset` is the first parameter for which a negative is *meaningful* — it
 * anchors the projection a month back, against the bars that actually followed
 * — and a grammar that cannot say it is a grammar that cannot store it.
 */
// A parameter segment is a number or a word, and a word may be **uppercase**:
// breadth's list is a group code (`brUVol_SP500`). Lowercase-only made every
// breadth row miss its catalog entry and print the raw alias as its name.
// **And a word may start with a digit** — new highs' window is `12M`. Words had
// to start with a letter, so `brNh_SP500_12M` matched nothing: no entry, so no
// parameter editor (the list could not be changed) and the raw alias as its
// name (owner, 2026-10-03). A plain number still takes the first branch.
// **Then an optional timeframe**, `~w` or `~m` (group 3): weekly RSI on a daily
// chart (`qf/higher_tf.py`; owner, 2026-11-02). The same family and parameters,
// a different row.
const ALIAS = /^([A-Za-z][A-Za-z0-9]*)((?:_(?:-?\d+(?:\.\d+)?|[A-Za-z0-9]+))*)(?:~([wm]))?(?:~fwd)?$/;

/** A higher timeframe an indicator can be drawn on, over a daily chart. */
export type Timeframe = "w" | "m";

/** The legend's and the row's mark for a timeframe: `RSI 14 · W`. */
export const TIMEFRAME_MARK: Record<Timeframe, string> = { w: "W", m: "M" };

/** `rsi_14~w` → `"w"`; anything else → undefined. */
export function timeframeOf(alias: string | undefined): Timeframe | undefined {
  const tf = alias ? ALIAS.exec(alias)?.[3] : undefined;
  return tf === "w" || tf === "m" ? tf : undefined;
}

/** `alias` on `tf` (or on the chart's own, `undefined`), any old suffix replaced. */
export function withTimeframe(alias: string, tf: Timeframe | undefined): string {
  const bare = alias.replace(/~[wm]$/, "");
  return tf ? `${bare}~${tf}` : bare;
}

/**
 * What an alias appends to say "the same indicator, over projected bars".
 *
 * `ema_20~fwd` is a second *rendering* of `ema_20`, not a second series — which
 * is what keeps it out of the catalog, out of the DSL registry and out of the
 * three mirrors that would otherwise each need a row. The server spells it the
 * same way (`chart_indicators.FORWARD_SUFFIX`); the two must agree or the twin
 * is a plot that is requested and never answered.
 */
export const FORWARD_SUFFIX = "~fwd";

/**
 * The plots that continue this chart's indicators past the last bar.
 *
 * **Derived on every build, never stored** — the rule reference `Level`s already
 * follow (D8: view state derivable from the model is not persisted). Storing
 * them would mean toggling the setting wrote and deleted N plots through the
 * workspace, each twin would be separately styleable and removable (a yellow
 * EMA you can delete while the line it continues stays), and a saved workspace
 * would carry plots that only make sense while a projection exists.
 *
 * Read by **both** the request builder and the renderer, so the columns asked
 * for and the series drawn cannot disagree — the failure that produces is a
 * plot with no data, which renders as an empty series rather than as an error.
 *
 * Empty unless three things hold, and each is a deliberate refusal:
 *
 * - the chart asked for it (`projectForward`);
 * - a **seasonality path** is on the chart, because that is what supplies the
 *   projected closes. Inventing one would be a forecast nobody asked for;
 * - the family is `projectable` — the server's call, since it follows from what
 *   the indicator reads rather than from anything a client can see.
 */
export function projectedTwins(
  group: ChartGroup,
  catalog: readonly IndicatorEntry[],
): { paneId: string; plot: Plot }[] {
  if (!group.projectForward) return [];

  const panes = group.panes ?? [];
  const hasPath = panes.some((pane) =>
    (pane.plots ?? []).some((p) => p.outputAlias?.startsWith("seasPath")),
  );
  if (!hasPath) return [];

  const byOutput = new Map<string, IndicatorEntry>();
  for (const entry of catalog) {
    for (const output of entry.outputs) byOutput.set(output.name.toLowerCase(), entry);
  }

  const out: { paneId: string; plot: Plot }[] = [];
  for (const pane of panes) {
    // **Idempotent, and it has to be.** These are derived on every build, and
    // folding a group that already carries its twins must not add a second set.
    // Skipping aliases that *end* in the suffix is not enough — that skips the
    // twin, while its source is still there and gets twinned again. Measured by
    // applying the fold twice: two `ema_20~fwd` plots, both drawn, one of them
    // unreachable by anything keyed on the alias.
    const seen = new Set<string>(
      (pane.plots ?? []).map((p) => p.outputAlias).filter((a): a is string => !!a),
    );
    for (const plot of pane.plots ?? []) {
      const alias = plot.outputAlias;
      if (!alias || BASE_ALIASES.has(alias) || alias.endsWith(FORWARD_SUFFIX)) continue;
      // A weekly series on a daily chart has no projection on the server.
      if (timeframeOf(alias)) continue;
      // A band is drawn from two columns and shares its primary with the line
      // above it; twinning it would ask for a fill of a forecast, which is ink
      // over a shape whose edges are already drawn.
      if (plot.type === "band" || plot.type === "signal") continue;
      const head = ALIAS.exec(alias)?.[1]?.toLowerCase();
      const entry = head ? byOutput.get(head) : undefined;
      if (!entry?.projectable) continue;
      // A projected bar carries no volume, so a family pointed at it by its
      // `source` has no twin — the server refuses `rsi_14_volume~fwd` too.
      if (/_volume(_|$)/.test(alias)) continue;
      const twin = alias + FORWARD_SUFFIX;
      if (seen.has(twin)) continue;
      seen.add(twin);
      out.push({
        paneId: pane.id,
        plot: {
          // Derived, so the id is derived too: stable across rebuilds, and
          // impossible to collide with a minted ULID.
          id: `${plot.id}~fwd`,
          order: (plot.order ?? 0) + 0.25,
          type: plot.type,
          ...(plot.indicatorRef ? { indicatorRef: plot.indicatorRef } : {}),
          outputAlias: alias + FORWARD_SUFFIX,
          ...(plot.axisRef ? { axisRef: plot.axisRef } : {}),
          // `warn` is a token, never a hex, so it follows a theme switch (D13).
          // It is yellow in all six built-ins and already means "read this
          // differently", which is exactly the claim a forecast makes.
          style: { ...plot.style, stroke: "warn" },
        } as Plot,
      });
    }
  }
  return out;
}

/**
 * `group` with its forward twins folded into the panes they belong to.
 *
 * **One fold, not a parameter threaded everywhere.** The twins have to reach
 * three different readers — the request builder, the render plan and the value
 * legend — and every one of them already derives from `group.panes`. Handing
 * each a second list would be three places to forget it, and the failure is a
 * plot that is drawn and never fed, or fed and never drawn.
 *
 * Returns the group **by identity** when there is nothing to add, so a chart
 * without projection costs no re-render and no rebuild: `structuralKey` and
 * every memo downstream compare the same object they had before.
 */
export function withProjectedTwins(
  group: ChartGroup,
  catalog: readonly IndicatorEntry[],
): ChartGroup {
  const twins = projectedTwins(group, catalog);
  if (twins.length === 0) return group;
  const byPane = new Map<string, Plot[]>();
  for (const { paneId, plot } of twins) {
    byPane.set(paneId, [...(byPane.get(paneId) ?? []), plot]);
  }
  return {
    ...group,
    panes: group.panes.map((pane) => {
      const add = byPane.get(pane.id);
      return add ? { ...pane, plots: [...(pane.plots ?? []), ...add] } : pane;
    }),
  };
}

/**
 * The share a marker-only pane takes when it is added.
 *
 * A fraction of what an oscillator gets, because the content is a row of
 * triangles rather than a curve with a shape to read. Still a `fr` share and
 * still draggable — a pane sized to its content is not something the contract
 * can say, so this is a starting height and not a rule.
 */
const SIGNAL_PANE_HEIGHT = "0.3fr";

/**
 * The opacity a Volume-by-Price plot is minted with.
 *
 * **Written into the plot's style rather than defaulted in the renderer**, the
 * way a catalog band's fill carries its own: a value in `PlotStyle` is one the
 * style panel edits per chart, and a value baked into the shell is one nobody
 * can reach without a deploy. That distinction is the point of putting it here —
 * this indicator draws a dozen bars *over* the candles rather than beside them,
 * so the number is a readability call made by eye, and the band fills went
 * 0.08 → 0.12 → 0.25 → 0.12 before landing. Expect a correction, and make it
 * cost nothing.
 */
const VOLUME_BY_PRICE_OPACITY = 0.18;

/** Parameter values a caller has typed, by parameter name. Missing ones default. */
export type IndicatorParams = Readonly<Record<string, number | string | undefined>>;

/**
 * The parameters an entry will actually be added with, in declared order.
 *
 * Order is the whole point — the alias positions them, so `macd_5` means a fast
 * of 5 and nothing else. A blank or non-finite input falls back to the default
 * rather than reaching the alias as `NaN`, which would name a series no server
 * can answer.
 */
/** Trailing parameters an alias omits at their default — see `resolveParams`. */
const TRAILING_DEFAULTS = new Set(["source", "offset"]);

export function resolveParams(
  entry: IndicatorEntry,
  params: IndicatorParams = {},
): (number | string)[] {
  const values = entry.params.map((spec) => {
    const given = params[spec.name];
    if (spec.kind === "symbol" || spec.kind === "choice") {
      // Uppercased for a symbol, because the alias is matched server-side
      // against a bar file and `spy` finds nothing. A blank falls back to the
      // default rather than reaching the alias as an empty string, which would
      // name `close@` — a series no server can answer.
      const text = typeof given === "string" ? given.trim() : "";
      const value = text || String(spec.default);
      return spec.kind === "symbol" ? value.toUpperCase() : value;
    }
    return typeof given === "number" && Number.isFinite(given)
      ? given
      : Number(spec.default);
  });
  // **A default `source` or `offset` is left off** (owner, 2026-10-10). Both
  // are trailing parameters the server fills with their defaults, so `ema_20`
  // stays the one spelling of the EMA of the close — every alias minted before
  // they existed, and everything that matches one by string, sees no new name.
  // Only from the end: `ema_20_close_5` keeps its `close`, or the 5 would land
  // in the source's position. Every reader of a row's params falls back to the
  // default for a missing trailing value.
  while (values.length > 0) {
    const spec = entry.params[values.length - 1];
    if (!spec || !TRAILING_DEFAULTS.has(spec.name)) break;
    if (String(values[values.length - 1]) !== String(spec.default)) break;
    values.pop();
  }
  return values;
}

/** `("macdSignal", [12, 26, 9])` → `"macdSignal_12_26_9"`. */
export function outputAlias(output: string, params: readonly (number | string)[]): string {
  return [output, ...params.map((p) => String(p))].join("_");
}

/** `symbolHigh` → `high`. The output's own OHLC field, for `field = all`. */
function fieldOf(outputName: string): string {
  const tail = outputName.replace(/^(symbol|compare)/, "").toLowerCase();
  return ["open", "high", "low", "close", "volume"].includes(tail) ? tail : "close";
}

/**
 * The alias for one output, from the entry's template when it has one.
 *
 * A Symbol Indicator's series is not computed from this chart's bars — the
 * server reads that symbol and joins it on date — so its alias names the field
 * and the symbol (`close@SPY`) rather than an output and its parameters. The
 * template is the server's, for the same reason the catalog is: that is the end
 * which has to read the alias back.
 *
 * `field = all` is the one substitution that is not a lookup. It means the four
 * OHLC series, so it expands to the output's *own* field — which is what lets
 * one entry yield four plots a pane can draw as candles.
 */
export function templatedAlias(
  entry: IndicatorEntry,
  output: IndicatorOutputSpec,
  values: readonly (number | string)[],
): string {
  if (!entry.aliasTemplate) return outputAlias(output.name, values);
  const byName = new Map(entry.params.map((spec, i) => [spec.name, String(values[i] ?? "")]));
  // `all` is one candle, and `ohlc` is how its alias says so — `chart-feed`
  // substitutes each of the four fields back out of it when it builds the
  // request and the delta.
  if (byName.get("field") === "all") byName.set("field", "ohlc");
  return entry.aliasTemplate.replace(/\{(\w+)\}/g, (whole, key) => byName.get(key) ?? whole);
}

/** The `field` a Symbol Indicator was added with. `close` when it has none. */
function fieldParam(
  entry: IndicatorEntry,
  values: readonly (number | string)[],
): string {
  const index = entry.params.findIndex((spec) => spec.name === "field");
  return index >= 0 ? String(values[index]) : "close";
}

/**
 * The outputs an entry actually draws for these parameters.
 *
 * A Symbol Indicator on a single field adds **one** series, not four: the other
 * three outputs exist so `all` can expand, and emitting them for a single-field
 * pick would request three plots nobody asked for.
 *
 * **`all` is also one output, not four** — and that is the whole point of it.
 * Four line plots of the same symbol's open, high, low and close is not what
 * anybody means by "all"; a candle is. So `all` collapses to a single
 * candlestick whose alias says `ohlc`, and `lib/chart-feed.ts` expands that into
 * the four columns it is fed by. Four separate plots would also cost four
 * legend rows and four colours for one instrument.
 */
export function outputsFor(
  entry: IndicatorEntry,
  values: readonly (number | string)[],
): IndicatorOutputSpec[] {
  if (!entry.aliasTemplate) return entry.outputs;
  const field = fieldParam(entry, values);
  const first = entry.outputs[0];
  if (!first) return entry.outputs;
  if (field === "all") {
    return [{ ...first, plot: "candlestick" as PlotType, label: first.label }];
  }
  return entry.outputs.filter((output) => fieldOf(output.name) === field).slice(0, 1);
}

/** Every alias an entry would add, in output order. */
export function indicatorAliases(entry: IndicatorEntry, params: IndicatorParams = {}): string[] {
  const values = resolveParams(entry, params);
  return outputsFor(entry, values).map((output) => templatedAlias(entry, output, values));
}

/** The plots for one family, all pointing at the same axis. */
function plotsFor(
  entry: IndicatorEntry,
  values: readonly (number | string)[],
  axisRef: string | undefined,
  firstOrder: number,
  mintId: () => string,
): Plot[] {
  const lines = entry.outputs.map((output, i) => ({
    id: mintId(),
    order: firstOrder + i,
    type: output.plot as PlotType,
    // The registry key, not the label: it is what `/ind/catalog` calls this
    // family, so a plot can be traced back to the entry that produced it.
    indicatorRef: entry.name,
    outputAlias: outputAlias(output.name, values),
    ...(axisRef ? { axisRef } : {}),
    // A signal has to say where its markers hang, and `pane` is the only anchor
    // that needs nothing else on the chart — a `series` anchor names a plot, and
    // an indicator being added has no way to know which one the user meant.
    // Changing it later is an edit to a plot that exists.
    ...(output.plot === "signal"
      ? {
          signal: {
            anchor: "pane" as const,
            // Outputs the catalog puts in one row share a lane — DOTS' squeeze.
            ...(output.signalRow ? { row: output.signalRow } : {}),
            // Spaced rows — Pressure's Hindenburg 10 down to Defib 1.
            ...(output.signalLevel ? { level: output.signalLevel } : {}),
          },
          // **A flag's colour is its up colour.** Markers paint with the
          // palette's up/down pair and never read `stroke`, so a flag — which
          // only fires 1 — would otherwise draw every event in `positive`
          // whatever the catalog said (Pressure, owner, 2026-10-03).
          ...(output.eventKind === "flag" && output.stroke
            ? { palette: { upColor: output.stroke } }
            : {}),
        }
      : {}),
    style: {
      stroke: output.stroke,
      // **1.5 is a *stroke* width, and the one output that has no stroke must
      // not arrive carrying one.** Volume-by-Price draws twenty-four filled
      // boxes, and `paintVolumeByPrice` reads `thickness ?? 0` precisely so a
      // bar that never asked for an outline does not gain one — its comment
      // says a zone edge at every boundary would fence the pane, which is
      // exactly what a width written here produces, since the default can then
      // never be reached. An absence rather than a zero: blank is what the
      // width spinner means by "this shape's own default", so a user who does
      // want the zones outlined can still type a number and get one.
      //
      // A pattern's lines are drawn over the candles and have to read against
      // them, so they start at 3 (owner, 2026-10-05) — the same width every
      // other line can be set to.
      ...(output.plot === "volumeByPrice"
        ? {}
        : {
            thickness:
              output.thickness !== undefined
                ? output.thickness
                : output.plot !== "geometry"
                ? 1.5
                : // Bar Intelligence's levels are thin dotted lines under their
                  // labels (docs/plans/bar-intelligence.md); a pattern's 3 would
                  // bury the candles they are measured against.
                  output.name === "barIntel" || output.name === "paLevels"
                  ? 1
                  : // Seasonality hits: 8 (owner, 2026-10-23), the user's to change.
                    output.name === "seasHits"
                    ? 8
                    : PATTERN_THICKNESS,
          }),
      ...(output.dash ? { dashStyle: output.dash } : {}),
      ...(output.digital ? { digital: true } : {}),
      ...(output.markerShape ? { markerShape: output.markerShape } : {}),
      ...(output.markerSize ? { markerSize: output.markerSize } : {}),
      ...(output.markerText ? { markerText: output.markerText } : {}),
      // The one output that arrives translucent, because it is the one drawn on
      // top of the candles instead of among them — see the constant for why the
      // number lives on the plot and not in the renderer.
      ...(output.plot === "volumeByPrice" ? { opacity: VOLUME_BY_PRICE_OPACITY } : {}),
    },
    // `positive`/`negative` are the tokens the candles already use, so a MACD
    // histogram is the same green as an up bar by construction rather than by
    // someone remembering to match them.
    ...(output.colorMode === "quad"
      ? { palette: QUAD_DEFAULTS }
      : output.colorMode
        ? {
            palette: {
              upColor: "positive",
              downColor: "negative",
              colorMode: output.colorMode,
            },
          }
        : {}),
    // A paint bar colours candle bodies: the WPF app's orange on +1 and light
    // blue on -1, as theme tokens so a theme switch carries them.
    ...(output.plot === "paint"
      ? { palette: { upColor: "accent.orange", downColor: "accent.blue" } }
      : {}),
  }));

  if (!entry.bands?.length) return lines;

  // A fill is an extra plot **underneath** the lines, not a replacement for
  // them: upper and lower keep their own stroke, width and hide toggle, and the
  // band is one more row in the style editor that can be turned off on its own.
  //
  // `order` puts them first so the lines draw over them; a fill painted last
  // would wash out the edges it is bounding. They are spaced inside the half
  // step before `firstOrder` so several bands keep a stable order among
  // themselves without ever colliding with a line's.
  const step = 0.5 / (entry.bands.length + 1);
  const bands = entry.bands.map((band, i) => ({
    id: mintId(),
    order: firstOrder - 0.5 + i * step,
    type: "band" as PlotType,
    indicatorRef: entry.name,
    outputAlias: bandEdgeAlias(band.upper, values),
    outputAlias2: bandEdgeAlias(band.lower, values),
    ...(axisRef ? { axisRef } : {}),
    style: {
      stroke: "transparent",
      thickness: 0,
      opacity: band.opacity,
      ...(band.fill ? { fill: band.fill } : {}),
    },
    // A paired fill changes colour at the crossing rather than only changing
    // width, which for a cloud is the whole reading. Same two tokens
    // `addBandBetween` uses, so a declared band and a composed one cannot end up
    // meaning different things by the same colours.
    ...(band.paired
      ? { palette: { upColor: "positive", downColor: "negative" } as PlotPalette }
      : {}),
  }));
  return [...bands, ...lines];
}

/**
 * One edge of a catalog band — an output alias, or a base column left alone.
 *
 * A fill between price and an indicator names `close` as one of its edges, and
 * `close` is not an output: it is in every price frame already, so appending the
 * family's parameters to it would mint `close_22_3_1`, a series the server has
 * never heard of. The feed then leaves it out of the response and the band draws
 * its far edge against zero — a shape that renders confidently and floods the
 * pane.
 *
 * `BASE_ALIASES` is the same set `bandCandidates` refuses to offer the user, for
 * the opposite reason: a user cannot *pick* price as a band edge because a
 * candlestick is not a line, while the catalog may *declare* it because the
 * catalog knows the fill is what the indicator means.
 */
function bandEdgeAlias(edge: string, values: readonly (number | string)[]): string {
  return BASE_ALIASES.has(edge) ? edge : outputAlias(edge, values);
}

/**
 * Does a catalog edge name the alias a plot actually carries?
 *
 * The comparison has to ignore the parameters, because the catalog says
 * `ceLong` where the plot says `ceLong_22_3_1` — and a base column says the same
 * thing on both sides. Only used to tell a family's several bands apart, which
 * is why matching the head is enough: two of them never share one.
 */
function bandEdgeMatches(edge: string, alias: string | undefined): boolean {
  if (!alias) return false;
  if (BASE_ALIASES.has(edge)) return edge === alias;
  return ALIAS.exec(alias)?.[1]?.toLowerCase() === edge.toLowerCase();
}

/**
 * The pane's own Y axis, from the catalog rather than from a hardcoded default.
 *
 * This used to be `autoRange: "always"` with `format: "0.00"` for every
 * indicator that ever got its own pane, which is right for about half of them.
 * A bounded oscillator auto-ranged is the case that actually misleads: RSI at 62
 * on an axis fitted to 58–64 looks pinned to its ceiling, and the chart is drawn
 * perfectly.
 *
 * A **fixed range clears `autoRange`**, because the two contradict each other
 * and `always` would simply refit over whatever was written. A server that sends
 * no `axis` — an older one, or a family that genuinely wants to auto-range —
 * lands exactly on the previous behaviour.
 */
export function axisFor(entry: IndicatorEntry, id: string): AxisInfo {
  const spec = entry.axis;
  const bounded = spec?.min !== undefined && spec?.max !== undefined;
  return {
    id,
    side: "right",
    autoRange: bounded ? "never" : "always",
    // **Read from the spec, not hardcoded**, and the hardcoded `"linear"` here
    // was the single line that decided what a log-scaled family renders as. The
    // contract has carried `AxisInfo.scale` from the beginning and `plan/axis.ts`
    // has always honoured it, so the whole path worked except its one producer —
    // which is the shape of gap that survives a typecheck and every test.
    scale: spec?.scale ?? "linear",
    format: spec?.format ?? "0.00",
    ...(bounded ? { visibleRange: { min: spec!.min!, max: spec!.max! } } : {}),
    grid: { minorsPerMajor: 2 },
  };
}

/**
 * Add an indicator to a chart group, returning a new one.
 *
 * An overlay whose group has no price pane gets a pane of its own instead of
 * being dropped: "nothing happened" is the worst answer to a click, and a chart
 * with no candles has no price scale to overlay in the first place.
 */
/** What a new smoother is, until the row says otherwise (owner, 2026-10-06). */
export const DEFAULT_SMOOTHER: { kind: SmoothKind; length: number } = { kind: "sma", length: 20 };

/**
 * Add a moving average of one plot — indicator on indicator (owner,
 * 2026-10-06). Same pane and scale as its source, dashed, in the first series
 * colour the pane is not already using, listed right after it. Returns `group`
 * by identity for a plot that is not there, is itself a smoother (one level),
 * or already has exactly this average.
 */
export function addSmoother(
  group: ChartGroup,
  sourcePlotId: string,
  kind: SmoothKind,
  length: number,
  mintId: () => string,
): ChartGroup {
  const pane = group.panes.find((p) => (p.plots ?? []).some((plot) => plot.id === sourcePlotId));
  const source = pane?.plots?.find((plot) => plot.id === sourcePlotId);
  if (!pane || !source?.outputAlias || splitSmoothed(source.outputAlias)) return group;
  const alias = `${source.outputAlias}~${kind}${Math.round(length)}`;
  if (!splitSmoothed(alias)) return group;
  if ((pane.plots ?? []).some((plot) => plot.outputAlias === alias)) return group;

  const used = new Set((pane.plots ?? []).map((plot) => plot.style?.stroke));
  const stroke = SERIES_TOKENS.find((t) => !used.has(t)) ?? "series2";
  const plot: Plot = {
    id: mintId(),
    type: "line",
    ...(source.indicatorRef ? { indicatorRef: source.indicatorRef } : {}),
    outputAlias: alias,
    ...(source.axisRef ? { axisRef: source.axisRef } : {}),
    style: { stroke, thickness: 1.5, dashStyle: "dashed" },
  };
  const plots = [...(pane.plots ?? [])];
  plots.splice(plots.indexOf(source) + 1, 0, plot);
  return { ...group, panes: group.panes.map((p) => (p === pane ? { ...pane, plots } : p)) };
}

/** Change a smoother's kind or length — a rename of its alias, its source kept. */
export function editSmoother(group: ChartGroup, plotId: string, kind: SmoothKind, length: number): ChartGroup {
  const pane = group.panes.find((p) => (p.plots ?? []).some((plot) => plot.id === plotId));
  const plot = pane?.plots?.find((p) => p.id === plotId);
  const found = splitSmoothed(plot?.outputAlias);
  if (!pane || !plot || !found) return group;
  const alias = `${found.source}~${kind}${Math.round(length)}`;
  if (alias === plot.outputAlias || !splitSmoothed(alias)) return group;
  if ((pane.plots ?? []).some((p) => p.outputAlias === alias)) return group;
  return {
    ...group,
    panes: group.panes.map((p) =>
      p === pane
        ? { ...p, plots: (p.plots ?? []).map((q) => (q.id === plotId ? { ...q, outputAlias: alias } : q)) }
        : p,
    ),
  };
}

const SERIES_TOKENS = ["series1", "series2", "series3", "series4", "series5", "series6"];

/**
 * Change Δ ×4's colours, as the owner's reference app draws them (2026-10-06):
 * cyan above zero and rising, blue above and falling, red below and falling,
 * magenta below and rising — as theme tokens, so a theme switch carries them.
 */
export const QUAD_DEFAULTS = {
  upColor: "accent.teal",
  upFadeColor: "series1",
  downColor: "negative",
  downFadeColor: "series3",
  colorMode: "quad",
} as const;

/**
 * Whether an indicator opens on the price pane, on a hidden scale of its own,
 * rather than in the pane the catalog gives it (owner, 2026-10-05).
 *
 * The universe ranks — `rank*` and their vs-SPY twins `rRank*` — are 0–100
 * percentiles read *against the candles*: in a pane of their own they cost a
 * pane's height to say where a stock stands, and on the price axis they would
 * flatten into a line at the bottom. Their own scale, undrawn, puts the line
 * across the candles without an axis taking the candles' width. Earnings Lift
 * and Industry/Sector Strength share the category and are not ranks.
 *
 * Only where it *opens*: Move to takes it anywhere afterwards, and the row's ⚙
 * changes the scale. A client rule rather than a catalog field, because it is
 * how this app lays a chart out, not a fact about the series.
 */
export function opensOnPriceHidden(entry: IndicatorEntry): boolean {
  return entry.placement === "own" && /^r?[rR]ank/.test(entry.name) && entry.category === "Universe Rank";
}

export function addIndicator(
  group: ChartGroup,
  entry: IndicatorEntry,
  params: IndicatorParams,
  mintId: () => string,
): ChartGroup {
  const added = addAsCataloged(group, entry, params, mintId);
  if (added === group || !opensOnPriceHidden(entry)) return added;
  const price = added.panes.find((pane) => pane.role === "price");
  const before = new Set(group.panes.map((pane) => pane.id));
  const fresh = added.panes.find((pane) => !before.has(pane.id));
  if (!price || !fresh) return added;
  const ids = (fresh.plots ?? []).map((plot) => plot.id);
  const moved = movePlotsToPane(added, { plotIds: ids, target: price.id, scale: "hidden" }, mintId);
  // A second Rank joins the first one's scale rather than printing the same
  // 0–100 again (owner, 2026-10-06: automatic). Matched on the fixed range, so
  // it is any same-ranged scale, not only another Rank's.
  const pane = moved.panes.find((p) => p.id === price.id);
  const partner = pane && matchingScale({ ...pane, plots: (pane.plots ?? []).filter((p) => !ids.includes(p.id)) }, axisFor(entry, "x"));
  return partner ? joinScale(moved, ids, partner.id) : moved;
}

function addAsCataloged(
  group: ChartGroup,
  entry: IndicatorEntry,
  params: IndicatorParams,
  mintId: () => string,
): ChartGroup {
  const values = resolveParams(entry, params);
  const price = group.panes.find((pane) => pane.role === "price");

  if (entry.placement === "price" && price) {
    const existing = price.plots ?? [];
    // That pane's axis, never a new one — see the header. The fallback reads it
    // off a sibling plot for a hand-built pane that carries no `axes`.
    const axisRef = price.axes?.[0]?.id ?? existing[0]?.axisRef;
    const plots = [...existing, ...plotsFor(entry, values, axisRef, existing.length, mintId)];
    return {
      ...group,
      panes: group.panes.map((pane) => (pane === price ? { ...pane, plots } : pane)),
    };
  }

  // A family that draws only markers gets a strip, not a pane: its content is
  // one row of triangles per output, and a `1fr` share of the chart would give a
  // dozen pixels of information the same height as an oscillator. It also needs
  // no axis of its own — the renderer builds a hidden lane axis for the pane,
  // and an ordinary one beside it would print `0.00` labels against a scale that
  // means lane indices.
  const markersOnly = entry.outputs.every((output) => output.plot === "signal");
  if (markersOnly) {
    const pane: Pane = {
      id: mintId(),
      role: "indicator",
      order: group.panes.length,
      height: SIGNAL_PANE_HEIGHT,
      axes: [],
      plots: plotsFor(entry, values, undefined, 0, mintId),
      annotations: [],
    };
    return { ...group, panes: [...group.panes, pane] };
  }

  const axisId = mintId();
  const pane: Pane = {
    id: mintId(),
    role: "indicator",
    order: group.panes.length,
    // The same share the volume pane takes. A pane sized to its content is not
    // something the contract can say, and every oscillator wants about this.
    height: "1fr",
    axes: [axisFor(entry, axisId)],
    plots: plotsFor(entry, values, axisId, 0, mintId),
    annotations: [],
  };
  return { ...group, panes: [...group.panes, pane] };
}

/**
 * Remove by pane id (an own-pane indicator) or by output alias (one overlay
 * series). Anything unknown returns the group unchanged, identity included.
 *
 * **The price pane survives losing its last plot.** It owns the X axis labels
 * and the scale every overlay hangs off, so a chart that dropped it would have
 * nowhere to put the next indicator and no candles to put back. An *indicator*
 * pane emptied by the same call does go: an empty pane is dead height with no
 * way to fill it.
 */
/**
 * Two plots in one pane that a band could be drawn between.
 *
 * The catalog declares a band for the five channel families, because a channel
 * *is* two edges and nobody wants Bollinger without the shading. This is the
 * other half: **any two series sharing an axis can be filled between**, and the
 * pairs worth having are ones no catalog could enumerate — RSI against its own
 * average, a fast moving average against a slow one, MACD against its signal.
 *
 * Nothing new is needed to express it. `Plot.outputAlias2` names a second
 * series and does not care where it came from; `chart-feed` requests both
 * columns and pairs them; the renderable is the same `FastBandRenderableSeries`
 * a channel uses. Only the *composition* was missing.
 */
export interface BandCandidate {
  paneId: string;
  plotId: string;
  alias: string;
  label: string;
}

/** Plot types a band can be drawn between — a shape with one y per bar. */
const FILLABLE = new Set<PlotType>(["line", "mountain", "column", "histogram"]);

/**
 * Every plot a band could use as an edge, grouped by pane.
 *
 * Grouped because a band is only meaningful **within one pane**: the two series
 * must share a y axis, or the fill is between two numbers measured in different
 * units and the shape it draws means nothing. That is also why this returns
 * pane ids rather than a flat list — the caller has to offer pairs, not plots.
 */
export function bandCandidates(
  group: ChartGroup,
  catalog: readonly IndicatorEntry[] = [],
): Map<string, BandCandidate[]> {
  const byOutput = new Map<string, IndicatorEntry>();
  for (const entry of catalog) {
    for (const output of entry.outputs) byOutput.set(output.name.toLowerCase(), entry);
  }

  const out = new Map<string, BandCandidate[]>();
  for (const pane of group.panes) {
    const usable: BandCandidate[] = [];
    for (const plot of pane.plots ?? []) {
      const alias = plot.outputAlias;
      if (!alias || !FILLABLE.has(plot.type)) continue;
      if (BASE_ALIASES.has(alias)) continue;
      const head = ALIAS.exec(alias)?.[1]?.toLowerCase();
      const entry = head ? byOutput.get(head) : undefined;
      usable.push({
        paneId: pane.id,
        plotId: plot.id,
        alias,
        label:
          entry?.outputs.find((o) => o.name.toLowerCase() === head)?.label ?? alias,
      });
    }
    // One plot cannot be a pair.
    if (usable.length >= 2) out.set(pane.id, usable);
  }
  return out;
}

/**
 * Fill between two series that are already on the chart.
 *
 * The band goes **under** them (`order` below the lowest of the two) so the
 * lines it is bounding stay legible on top of their own fill — the same reason
 * a catalog channel's fill is ordered first.
 *
 * It carries a **paired palette**, which is what makes this different from a
 * channel: the fill takes one colour where the first series leads and another
 * where the second does, so an RSI crossing its average changes the band's
 * colour at the crossing rather than only its width. `positive`/`negative` are
 * the tokens the candles already use, so it agrees with the rest of the chart
 * by construction (D13).
 *
 * Refuses a pair that is not in one pane, and refuses a duplicate: two bands on
 * the same pair is one drawn exactly on top of another, which reads as a
 * darker fill nobody can account for or remove.
 */
export function addBandBetween(
  group: ChartGroup,
  upperAlias: string,
  lowerAlias: string,
  mintId: () => string,
): ChartGroup {
  if (upperAlias === lowerAlias) return group;

  const pane = group.panes.find((p) => {
    const aliases = new Set((p.plots ?? []).map((plot) => plot.outputAlias));
    return aliases.has(upperAlias) && aliases.has(lowerAlias);
  });
  if (!pane) return group;

  const plots = pane.plots ?? [];
  const already = plots.some(
    (p) =>
      p.type === "band" &&
      ((p.outputAlias === upperAlias && p.outputAlias2 === lowerAlias) ||
        (p.outputAlias === lowerAlias && p.outputAlias2 === upperAlias)),
  );
  if (already) return group;

  const edges = plots.filter(
    (p) => p.outputAlias === upperAlias || p.outputAlias === lowerAlias,
  );
  const axisRef = edges[0]?.axisRef;
  const lowestOrder = Math.min(...edges.map((p) => p.order ?? 0));

  const band: Plot = {
    id: mintId(),
    order: lowestOrder - 0.5,
    type: "band" as PlotType,
    outputAlias: upperAlias,
    outputAlias2: lowerAlias,
    ...(axisRef ? { axisRef } : {}),
    style: { stroke: "transparent", thickness: 0 },
    palette: { upColor: "positive", downColor: "negative" },
  };

  return {
    ...group,
    panes: group.panes.map((p) => (p === pane ? { ...p, plots: [band, ...plots] } : p)),
  };
}

/** A band the user drew between two series, rather than one a family declared. */
export interface ComposedBand {
  plotId: string;
  paneId: string;
  upper: string;
  lower: string;
}

/**
 * The bands a user composed, told apart from the ones a family brought with it.
 *
 * The discriminator is `indicatorRef`, and its **absence is the information**:
 * `plotsFor` stamps every plot it creates with the catalog entry that produced
 * it, so a band with no `indicatorRef` is by construction one that came from
 * nowhere in the catalog. That matters because the two are removed differently
 * — a channel's fill goes when its family does, and a composed band has to be
 * removable on its own without taking the two series it was drawn between.
 */
export function composedBands(group: ChartGroup): ComposedBand[] {
  const out: ComposedBand[] = [];
  for (const pane of group.panes) {
    for (const plot of pane.plots ?? []) {
      if (plot.type !== "band" || plot.indicatorRef) continue;
      if (!plot.outputAlias || !plot.outputAlias2) continue;
      out.push({
        plotId: plot.id,
        paneId: pane.id,
        upper: plot.outputAlias,
        lower: plot.outputAlias2,
      });
    }
  }
  return out;
}

/**
 * Remove one plot by id — the way a user-composed band is taken off again.
 *
 * `removeIndicator` addresses a *family*, by pane or by alias, which is right
 * for something the catalog put there and wrong for a band: its two aliases
 * belong to the plots it was drawn between, so removing by alias would take
 * those with it.
 */
export function removePlot(group: ChartGroup, plotId: string): ChartGroup {
  return {
    ...group,
    panes: group.panes.map((pane) => {
      const plots = pane.plots ?? [];
      if (!plots.some((p) => p.id === plotId)) return pane;
      return withoutUnusedAxes(pane, plots.filter((p) => p.id !== plotId));
    }),
  };
}

export function removeIndicator(group: ChartGroup, target: string): ChartGroup {
  return withoutOrphanSmoothers(removeTarget(group, target));
}

/**
 * Delete one line of a family — Adv NYSE's A−D, leaving Adv and Dec (owner,
 * 2026-10-06). By plot id, like {@link removePlot}, and a smoother built on the
 * line goes with it, as it does with the whole family.
 */
export function removeOutput(group: ChartGroup, plotId: string): ChartGroup {
  return withoutOrphanSmoothers(removePlot(group, plotId));
}

/**
 * The family's lines that are not on the chart — the "Add back" chips.
 *
 * Empty for a family the catalog cannot name and for a smoother, which is one
 * line by construction. Matched on the alias head, case-insensitively, the way
 * every other lookup in this file pairs an alias with its catalog output.
 */
export function removedOutputs(row: AppliedIndicator): IndicatorOutputSpec[] {
  if (!row.entry || row.smoothOf) return [];
  const on = new Set(row.aliases.map((alias) => ALIAS.exec(alias)?.[1]?.toLowerCase()));
  return outputsFor(row.entry, row.params).filter((output) => !on.has(output.name.toLowerCase()));
}

/**
 * Put a deleted line back, styled as the catalog adds it, on the family's axis
 * and drawn after its siblings. Returns the group by identity when the family
 * or the output cannot be found, or the line is already there.
 */
export function addOutputBack(
  group: ChartGroup,
  row: AppliedIndicator,
  outputName: string,
  mintId: () => string,
): ChartGroup {
  const entry = row.entry;
  const output = removedOutputs(row).find((o) => o.name === outputName);
  if (!entry || !output) return group;
  const pane = group.panes.find((p) => p.id === row.paneId);
  if (!pane) return group;
  const mine = new Set(row.plots.map((p) => p.plotId));
  const siblings = (pane.plots ?? []).filter((plot) => mine.has(plot.id));
  const axisRef = siblings[0]?.axisRef;
  const order = Math.max(-1, ...(pane.plots ?? []).map((plot) => plot.order ?? 0)) + 1;
  const plots = plotsFor({ ...entry, outputs: [output] }, row.params, axisRef, order, mintId);
  return {
    ...group,
    panes: group.panes.map((p) => (p.id === row.paneId ? { ...p, plots: [...(p.plots ?? []), ...plots] } : p)),
  };
}

/**
 * Smoothers whose source is no longer on the chart (owner, 2026-10-06: a
 * smoother goes with its source). Checked against what is *left*, so removing
 * one of two instances on the same alias keeps the smoother the other feeds.
 */
function withoutOrphanSmoothers(group: ChartGroup): ChartGroup {
  const aliases = new Set(group.panes.flatMap((p) => (p.plots ?? []).map((plot) => plot.outputAlias)));
  const orphan = (plot: Plot) => {
    const found = splitSmoothed(plot.outputAlias);
    // `close` is the candles, always there. `volume` is not any more: it can
    // be removed (owner, 2026-10-19), and its average goes with it.
    return found !== undefined && found.source !== "close" && !aliases.has(found.source);
  };
  if (!group.panes.some((p) => (p.plots ?? []).some(orphan))) return group;
  return {
    ...group,
    panes: group.panes.map((p) =>
      (p.plots ?? []).some(orphan) ? { ...p, plots: (p.plots ?? []).filter((plot) => !orphan(plot)) } : p,
    ),
  };
}

function removeTarget(group: ChartGroup, target: string): ChartGroup {
  const pane = group.panes.find((p) => p.id === target);
  if (pane) {
    // A pane this module created, or the volume pane (owner, 2026-10-19:
    // volume can be deleted, and added back from Indicators › Volume). The
    // price pane is never removed — a chart without its candles is not a state
    // the model has.
    if (pane.role !== "indicator" && pane.role !== "volume") return group;
    return { ...group, panes: group.panes.filter((p) => p !== pane) };
  }

  // A plot id or an alias. The picker sends plot ids for an overlay, since two
  // instances of one indicator share an alias; an alias still works.
  const hit = (plot: Plot) => plot.id === target || plot.outputAlias === target;
  const panes: Pane[] = [];
  let removed = false;
  for (const p of group.panes) {
    const plots = p.plots ?? [];
    if (!plots.some(hit)) {
      panes.push(p);
      continue;
    }
    removed = true;
    const kept = plots.filter((plot) => !hit(plot));
    if (kept.length === 0 && p.role === "indicator") continue;
    // An axis nothing draws on any more goes with its plot — volume's hidden
    // one (*Volume on price*) and an overlay's own visible scale alike.
    panes.push(withoutUnusedAxes(p, kept));
  }
  return removed ? { ...group, panes } : group;
}

/**
 * One indicator already on the chart — a *family*, not a series.
 *
 * MACD is three plots and one row: it was added as a group and it is removed as
 * a group, so listing three lines with three remove buttons would offer a state
 * (a signal line with no MACD) that nothing can produce on purpose.
 */
/**
 * One drawable series of an applied family, as the picker edits it.
 *
 * A *plot*, not a series of numbers: the whole point is that `plotId` addresses
 * the contract node the style actions take, so changing a colour is a workspace
 * mutation on an existing plot rather than a rebuild of the family.
 */
export interface AppliedPlot {
  plotId: string;
  alias: string;
  /** The catalog's output label (`"Signal"`), else the alias — never blank. */
  label: string;
  type: PlotType;
  style?: PlotStyle;
  palette?: PlotPalette;
  /** Drawn or not. Absent in the contract means drawn. */
  hidden?: boolean;
  /**
   * A signal that only ever fires 1 (catalog `eventKind: "flag"`) — one colour,
   * so the editor offers one picker, bound to the up colour the markers use.
   */
  flag?: boolean;
  /**
   * The catalog draws this output as a paint bar (HCD/LCD), so the row offers
   * Paint candles / Markers — the same column either way.
   */
  paintable?: boolean;
}

export interface AppliedIndicator {
  /** Stable across renders: the pane it lives in plus the family and its parameters. */
  key: string;
  /** Drawn on a higher timeframe than the chart's — `rsi_14~w` is `"w"`. */
  timeframe?: Timeframe;
  /** `"MACD 12, 26, 9"` — the catalog's label, and the parameters if it has any. */
  label: string;
  paneId: string;
  placement: "price" | "own";
  aliases: string[];
  /**
   * What to hand `removeIndicator`, folded left. An own-pane indicator is its
   * pane; an overlay is its aliases, since the price pane stays either way.
   */
  targets: string[];
  /**
   * Whether the row may be removed at all.
   *
   * False for the price row: nothing removes the candles. Volume is removable
   * (owner, 2026-10-19) and comes back from Indicators › Volume.
   */
  removable: boolean;
  /**
   * The catalog entry this family came from, when the catalog can name it.
   *
   * Carried rather than looked up again by the caller: without it there is no
   * parameter *order*, and the alias is positional — so an unknown family can be
   * listed and removed but cannot be re-parameterised, which is exactly the
   * distinction {@link editIndicatorParams} enforces.
   */
  entry?: IndicatorEntry;
  /**
   * Current parameter values in declared order, parsed back out of the alias.
   *
   * **Positional — one entry per declared parameter, in order, always.** The
   * editor pairs them with `entry.params` by index, so a value that is dropped
   * rather than kept slides every later parameter onto the wrong control.
   * Strings are ordinary here: a `choice` reaches the alias as a word.
   */
  params: (number | string)[];
  /** Every plot of the family, in draw order — one row each in the style editor. */
  plots: AppliedPlot[];
  /**
   * The axis this family's pane scales on, when it has one.
   *
   * Carried so the scale control can show what the axis *is* rather than storing
   * which button was pressed — `paneScaleOf` derives the mode, for the reason
   * `matchTemplate` and `screenerPlacement` derive theirs: a stored mode is a
   * second source of truth that a hand-edited range falsifies.
   */
  axis?: AxisInfo;
  /** A smoother (`obv~sma20`): the alias it averages. */
  smoothOf?: string;
  /** A smoother: the key of its source's row, which it is listed under. */
  smoothParent?: string;
}

/**
 * Re-parameterise an applied indicator: `macd_12_26_9` → `macd_5_35_5`.
 *
 * **The whole family is rewritten together, or nothing is.** The alias encodes
 * the parameters, so an edit is a rename of every plot in the family at once —
 * change MACD's line and leave its signal alone and the chart draws two
 * different indicators in one pane, one of which is labelled as the other. That
 * is why this takes the row rather than a plot, and why it returns a whole group.
 *
 * Plot ids, styles, order and axis refs all survive: only `outputAlias` moves.
 * Removing and re-adding would have been fewer lines and would have thrown away
 * every colour and width the user had set on the family, plus its pane — and the
 * pane is where the divider heights live.
 *
 * Refused, returning the group by identity:
 *  - a family the catalog cannot name (no parameter order to rewrite by);
 *  - an edit that changes nothing;
 *  - an edit whose new aliases collide with a plot already on the chart. Two
 *    plots on one alias is not a duplicate drawing — the feed keys its response
 *    by alias, so the second one silently gets no data at all.
 */
export function editIndicatorParams(
  group: ChartGroup,
  row: AppliedIndicator,
  params: IndicatorParams,
): ChartGroup {
  const entry = row.entry;
  if (!entry) return group;

  const values = resolveParams(entry, params);
  const renames = new Map<string, string>();
  for (const alias of row.aliases) {
    const head = ALIAS.exec(alias)?.[1];
    if (!head) return group;
    // A weekly row stays weekly when its parameters change.
    renames.set(alias, withTimeframe(outputAlias(head, values), timeframeOf(alias)));
  }

  return renameRow(group, row, renames);
}

/**
 * Draw a row on a higher timeframe — `rsi_14` → `rsi_14~w` — or back on the
 * chart's own (`undefined`). The Timeframe control in the row's editor (owner,
 * 2026-11-02). Every alias of the row moves together, and a smoother of it
 * follows, exactly as a parameter edit does.
 */
export function setIndicatorTimeframe(
  group: ChartGroup,
  row: AppliedIndicator,
  tf: Timeframe | undefined,
): ChartGroup {
  const renames = new Map<string, string>();
  for (const alias of row.aliases) renames.set(alias, withTimeframe(alias, tf));
  return renameRow(group, row, renames);
}

/** Rename one row's plots by id, its band edges and its smoothers' sources with it. */
function renameRow(group: ChartGroup, row: AppliedIndicator, renames: Map<string, string>): ChartGroup {
  if ([...renames].every(([from, to]) => from === to)) return group;

  // **This row's plots, by id — not every plot on its aliases.** A second
  // instance of the same indicator shares them, and renaming by alias moved both.
  const mine = new Set(row.plots.map((p) => p.plotId));
  const taken = new Set(
    group.panes.flatMap((p) =>
      (p.plots ?? [])
        .filter((plot) => !mine.has(plot.id))
        .map((plot) => plot.outputAlias)
        .filter((a): a is string => !!a),
    ),
  );
  if ([...renames.values()].some((alias) => taken.has(alias))) return group;

  return {
    ...group,
    panes: group.panes.map((pane) => {
      if (pane.id !== row.paneId) return pane;
      return {
        ...pane,
        plots: (pane.plots ?? []).map((plot) => {
          // A smoother of a renamed output keeps its suffix on the new name —
          // `obv_14~sma20` follows `obv_14` to `obv_21~sma20`.
          const smooth = !mine.has(plot.id) ? splitSmoothed(plot.outputAlias) : undefined;
          const renamedSource = smooth && renames.get(smooth.source);
          if (smooth && renamedSource) {
            return { ...plot, outputAlias: `${renamedSource}~${smooth.kind}${smooth.length}` };
          }
          if (!mine.has(plot.id)) return plot;
          const next = plot.outputAlias && renames.get(plot.outputAlias);
          // **A band's far edge moves with the near one.** `outputAlias2` carries
          // the same parameters and was not being rewritten, so re-parameterising
          // Bollinger from 20/2 to 50/3 left the fill spanning `bbandsUpper_50_3`
          // and `bbandsLower_20_2` — both aliases the server answers, so the band
          // drew a shape between two different indicators and looked fine. Same
          // failure as renaming half a family, one field further down.
          //
          // An edge naming a base column (`close`, for a fill between price and
          // an indicator) is not in `renames` and is left alone, which is right:
          // it carries no parameters to move.
          const next2 = plot.outputAlias2 && renames.get(plot.outputAlias2);
          if (!next && !next2) return plot;
          return {
            ...plot,
            ...(next ? { outputAlias: next } : {}),
            ...(next2 ? { outputAlias2: next2 } : {}),
          };
        }),
      };
    }),
  };
}

/** `[20, 2]` → `"20, 2"`; nothing at all for a family with no parameters. */
function paramSuffix(values: readonly string[]): string {
  return values.length ? ` ${values.join(", ")}` : "";
}

/**
 * The name an applied row is listed under.
 *
 * **A breadth row is named by its output, then its list and window, with
 * spaces** — `NH SP500 12M` (owner, 2026-10-03). Its outputs are often added
 * apart (NH in one pane, NL in another), so the family's name, "New Highs −
 * New Lows", would call both rows the same thing; the output's short label is
 * what tells them apart, and a list code reads as a word, not a number to be
 * comma-separated. Everything else keeps the family label (`MACD 12, 26, 9`).
 */
function rowLabel(entry: IndicatorEntry, head: string | undefined, values: readonly string[]): string {
  if (entry.category !== "Breadth") return `${entry.label}${paramSuffix(values)}`;
  const output = entry.outputs.find((o) => o.name.toLowerCase() === head)?.label ?? entry.label;
  return [output, ...values].join(" ");
}

/**
 * What the chart is currently drawing, as the picker lists it.
 *
 * Aliases are resolved through the catalog so a family's outputs collapse into
 * one row; an alias the catalog does not know keeps its own row, labelled by the
 * raw string. That is the offline case — with no catalog the demo chart's
 * `ema_20` and `ema_50` still list and still remove — and it is also what stops
 * a stale client hiding a plot it cannot name.
 */
/** The candle kinds, which draw the instrument rather than a reading of it. */
/** The default width of a pattern's lines — trendlines, cups, head and shoulders. */
export const PATTERN_THICKNESS = 3;

const isCandle = (type: PlotType): boolean => type === "candlestick" || type === "ohlc";

/**
 * One plot of a row that has no catalog entry behind it — price, or volume.
 *
 * Both are the chart's own furniture: named for what they are, since there is no
 * output label to look up, and carrying their style so the editor can still
 * restyle what is drawn.
 */
function basePlot(plot: Plot, alias: string, label: string): AppliedPlot {
  return {
    plotId: plot.id,
    alias,
    label,
    type: plot.type,
    style: plot.style,
    palette: plot.palette,
    hidden: plot.hidden ?? false,
  };
}

export function appliedIndicators(
  group: ChartGroup,
  catalog: readonly IndicatorEntry[],
): AppliedIndicator[] {
  const byOutput = new Map<string, IndicatorEntry>();
  for (const entry of catalog) {
    for (const output of entry.outputs) byOutput.set(output.name.toLowerCase(), entry);
  }

  const rows = new Map<string, AppliedIndicator>();
  const seen = new Map<string, number>();
  for (const pane of group.panes) {
    for (const plot of pane.plots ?? []) {
      const alias = plot.outputAlias;
      if (!alias) continue;

      // **A smoother is a row of its own, under its source** (owner,
      // 2026-10-06). Removed by plot id — `placement: "price"` is what makes the
      // × address the plot rather than the pane it shares — and named after its
      // source once every row exists, below.
      const smooth = splitSmoothed(alias);
      if (smooth) {
        const key = `${pane.id}:${alias}:${plot.id}`;
        const name = `${smooth.kind.toUpperCase()} ${smooth.length}`;
        rows.set(key, {
          key,
          label: name,
          paneId: pane.id,
          placement: "price",
          aliases: [alias],
          targets: [plot.id],
          removable: true,
          params: [],
          plots: [basePlot(plot, alias, name)],
          smoothOf: smooth.source,
        });
        continue;
      }

      // **Volume is listed, and it is the one base alias that is.** It has a
      // pane, a palette and a place in the stack, so the picker can reorder and
      // restyle it exactly as it does an oscillator — the two things the list is
      // for. `placement` follows the pane it is actually in, so the bars get a
      // grip when they have a pane of their own and none while they are drawn
      // over the candles, which is the same line every overlay is on.
      if (alias === VOLUME_ALIAS) {
        const ownPane = pane.role === "volume";
        const key = `${pane.id}:volume`;
        rows.set(key, {
          key,
          label: "Volume",
          paneId: pane.id,
          placement: ownPane ? "own" : "price",
          aliases: [alias],
          // Removable since 2026-10-19 (owner): its own pane goes whole; drawn
          // over the candles, the bars go by plot id. Indicators › Volume puts
          // it back ({@link addVolume}).
          targets: [ownPane ? pane.id : plot.id],
          removable: true,
          // No catalog entry and no parameters — the editor's parameter half
          // renders nothing for a row with neither, which is what leaves the
          // style rows as the whole of it.
          params: [],
          plots: [basePlot(plot, alias, "Volume")],
          // Only its own pane's axis. Overlaid, the first axis on the pane is
          // the *price* axis, and offering that as "the volume scale" would pin
          // the candles.
          ...(ownPane && pane.axes?.[0] ? { axis: pane.axes[0] } : {}),
        });
        continue;
      }

      // **The price pane is a row too, and without it the list cannot say
      // "top".** The candles were skipped as scenery, so a chart with no overlay
      // on them had no row standing for the price pane at all — and the drop
      // target for a drag is a *row*, so there was nothing to drop onto and an
      // oscillator could not be moved above the price. It is listed for what it
      // is rather than what it draws: a pane, in a place in the stack.
      if (pane.role === "price" && (PRICE_ALIASES.has(alias) || isCandle(plot.type))) {
        const key = `${pane.id}:price`;
        const applied = basePlot(plot, alias, "Price");
        const existing = rows.get(key);
        // Merged rather than a row each: `open`/`high`/`low`/`close` drawn
        // separately are still one price series in one pane.
        if (existing) {
          existing.aliases.push(alias);
          existing.plots.push(applied);
          continue;
        }
        rows.set(key, {
          key,
          label: "Price",
          paneId: pane.id,
          placement: "own",
          aliases: [alias],
          // Nothing removes the candles: `removeIndicator` refuses a pane whose
          // role is not `indicator`, and a chart without its price series is not
          // a state the model has.
          targets: [],
          removable: false,
          params: [],
          plots: [applied],
          // **No `axis`, deliberately.** What the price axis scales to is the
          // chart-level `Price axis` setting, so offering a pane-scale control
          // here would give two answers to one question — the same reason an
          // overlay row is not offered one.
        });
        continue;
      }

      if (BASE_ALIASES.has(alias)) continue;
      if (isCandle(plot.type)) continue;

      const match = ALIAS.exec(alias);
      const entry = (match && byOutput.get(match[1]!.toLowerCase())) || undefined;
      // Keyed by the family and its parameters, so the same indicator added
      // twice with different windows is two rows rather than one that removes
      // both.
      //
      // **And by instance, so the same indicator added twice with the *same*
      // window is two rows too.** Two SMA 20s merged into one row whose edit
      // renamed both, so they could never be told apart again (2026-10-03). The
      // nth plot on an alias belongs to the nth instance; a band is counted
      // apart from the line it shares an alias with, or Bollinger's fill would
      // read as a second Bollinger.
      const tf = match?.[3] as Timeframe | undefined;
      const base = entry
        ? `${pane.id}:${entry.name}:${match![2]}${tf ? `~${tf}` : ""}`
        : `${pane.id}:${alias}`;
      const mark = tf ? ` · ${TIMEFRAME_MARK[tf]}` : "";
      const seenKey = `${pane.id}|${alias}|${plot.type === "band"}`;
      const instance = seen.get(seenKey) ?? 0;
      seen.set(seenKey, instance + 1);
      const key = instance ? `${base}#${instance}` : base;
      // The catalog's own name for this output (`"Signal"`), so the style editor
      // says which of MACD's three lines a row is. Falls back to the alias for a
      // family nothing can name, which is the offline case.
      //
      // **A band is named by the band, not by the output it shares an alias
      // with.** A fill is drawn under its edge and feeds from the same column, so
      // looking the alias up among the outputs gave a fill its edge's name — two
      // rows reading "Long stop", one of them mysteriously without a width.
      const head = match?.[1]?.toLowerCase();
      const outputLabel =
        entry?.outputs.find((o) => o.name.toLowerCase() === head)?.label ?? alias;
      const bandLabel =
        plot.type === "band"
          ? entry?.bands?.find(
              (b) =>
                b.upper.toLowerCase() === head &&
                bandEdgeMatches(b.lower, plot.outputAlias2),
            )?.label
          : undefined;
      const applied: AppliedPlot = {
        plotId: plot.id,
        alias,
        label: `${bandLabel ?? outputLabel}${mark}`,
        type: plot.type,
        style: plot.style,
        palette: plot.palette,
        hidden: plot.hidden ?? false,
        ...(plot.type === "signal" &&
        entry?.outputs.find((o) => o.name.toLowerCase() === head)?.eventKind === "flag"
          ? { flag: true }
          : {}),
        ...(entry?.outputs.find((o) => o.name.toLowerCase() === head)?.plot === "paint"
          ? { paintable: true }
          : {}),
      };

      const existing = rows.get(key);
      if (existing) {
        existing.aliases.push(alias);
        existing.plots.push(applied);
        if (existing.placement === "price") existing.targets.push(plot.id);
        continue;
      }

      const values = match?.[2] ? match[2].split("_").filter(Boolean) : [];
      const own = pane.role === "indicator";
      rows.set(key, {
        key,
        label: entry ? `${rowLabel(entry, head, values)}${mark}` : alias,
        paneId: pane.id,
        ...(tf ? { timeframe: tf } : {}),
        placement: own ? "own" : "price",
        aliases: [alias],
        // An overlay by plot id, not alias: two instances share an alias, and
        // removing by it took both.
        targets: own ? [pane.id] : [plot.id],
        removable: true,
        entry,
        // **Positional, and never filtered.** These are read back by index —
        // `row.params[i]` against `entry.params[i]` — so dropping an entry
        // shifts every parameter after it onto the wrong control.
        //
        // It used to be `values.map(Number).filter(Number.isFinite)`, which was
        // invisible while every parameter was a number and wrong the moment one
        // was not: `seasPath_ytd_10` became `[10]`, so the *anchor* control
        // opened showing `10` and the year control fell back to its default.
        // Applying that then minted `seasPath_10_10` — an alias the server
        // refuses, so the column was left out and the plot drew nothing.
        //
        // A segment that reads as a number becomes one — the numeric inputs
        // want numbers and that half of the old behaviour was right — and one
        // that does not stays the word it is.
        params: values.map((v) =>
          v !== "" && Number.isFinite(Number(v)) ? Number(v) : v,
        ),
        plots: [applied],
        // The axis this family's lines are drawn against — its own when it was
        // moved in with Own scale, else the pane's first, the one `addIndicator`
        // writes. Absent on a hand-built pane that declares none, which is why
        // the scale control checks before offering itself.
        ...((pane.axes?.find((a) => a.id === plot.axisRef) ?? pane.axes?.[0])
          ? { axis: pane.axes!.find((a) => a.id === plot.axisRef) ?? pane.axes![0]! }
          : {}),
      });
    }
  }
  // "SMA 20 of OBV": the source's row, found by alias in the same pane.
  for (const row of rows.values()) {
    if (!row.smoothOf) continue;
    const parent = [...rows.values()].find(
      (r) => r.paneId === row.paneId && !r.smoothOf && r.aliases.includes(row.smoothOf!),
    );
    if (parent) {
      row.label = `${row.label} of ${parent.label}`;
      row.smoothParent = parent.key;
    }
  }

  // **A pane shared by two families removes by plot, not by pane** (Move to,
  // `lib/move-to-pane.ts`). Removing by pane took every family in it, so the ×
  // on one "% Above MA" row deleted its neighbour too.
  const perPane = new Map<string, number>();
  for (const row of rows.values()) {
    if (row.placement === "own" && row.removable) perPane.set(row.paneId, (perPane.get(row.paneId) ?? 0) + 1);
  }
  for (const row of rows.values()) {
    if (row.placement === "own" && row.removable && (perPane.get(row.paneId) ?? 0) > 1) {
      row.targets = row.plots.map((p) => p.plotId);
    }
  }
  return [...rows.values()];
}

/**
 * Indicators › Volume (owner, 2026-10-19): not a catalog family — the server
 * has no `volume` entry, the bars are the base column — but listed beside OBV so
 * a removed Volume has a way back. {@link addVolume} is what adding it does.
 */
export const VOLUME_ENTRY: IndicatorEntry = {
  name: VOLUME_ALIAS,
  label: "Volume",
  category: "Volume Indicators",
  placement: "own",
  description: "Shares traded per bar, coloured by direction",
  params: [],
  outputs: [],
};

/** Whether the chart draws volume anywhere — its own pane or over the candles. */
export function hasVolume(group: ChartGroup): boolean {
  return group.panes.some((p) => (p.plots ?? []).some((plot) => plot.outputAlias === VOLUME_ALIAS));
}

/**
 * Put the volume pane back, as a new chart has it, at the bottom of the stack.
 * The group by identity when volume is already drawn.
 */
export function addVolume(group: ChartGroup): ChartGroup {
  if (hasVolume(group)) return group;
  return { ...group, panes: [...group.panes, newVolumePane(group.panes.length)] };
}
